import React, { useEffect, useRef, useState } from 'react';
import { STooltip } from '@avalara/skylab-sdk/react';
import LANGUAGE_CONTROLLER from '../../controllers/languages';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import ApplicationMessages from '../inAppMessages/ApplicationMessages';
import Logger, { ALL_MESSAGES, LOG_LEVELS } from '../../utils/logger';

const MASKED_LICENSE_KEY = '*'.repeat(20);
const MASKED_CUSTOMER_ID = '***';
const FORM_NAME = 'API_LICENSE_KEY_RETRIEVE_SECTION';
const COPIED_RESET_DELAY_MS = 2000;
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);

const RetrieveLicenseKeySection = (props) => {
    const [licenseKey, setLicenseKey] = useState(null);
    const [customerId, setCustomerId] = useState(null);
    const [revealed, setRevealed] = useState(false);
    const [retrieving, setRetrieving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [customerIdCopied, setCustomerIdCopied] = useState(false);
    const copiedResetTimeoutRef = useRef(null);
    const customerIdCopiedResetTimeoutRef = useRef(null);

    const clearCopiedResetTimeout = () => {
        if (copiedResetTimeoutRef.current) {
            clearTimeout(copiedResetTimeoutRef.current);
            copiedResetTimeoutRef.current = null;
        }
    };

    const clearCustomerIdCopiedResetTimeout = () => {
        if (customerIdCopiedResetTimeoutRef.current) {
            clearTimeout(customerIdCopiedResetTimeoutRef.current);
            customerIdCopiedResetTimeoutRef.current = null;
        }
    };

    useEffect(() => () => {
        clearCopiedResetTimeout();
        clearCustomerIdCopiedResetTimeout();
    }, []);

    const handleToggleClick = async () => {
        if (revealed) {
            setRevealed(false);
            setLicenseKey(null);
            setCustomerId(null);
            setCopied(false);
            setCustomerIdCopied(false);
            clearCopiedResetTimeout();
            clearCustomerIdCopiedResetTimeout();
            return;
        }

        log.clearDictionaryError(ALL_MESSAGES.API_LICENSE_KEY_RETRIEVE_ERROR);
        setRetrieving(true);
        try {
            const result = await props.onRetrieveLicenseKey();
            setLicenseKey(result?.licenseKey);
            setCustomerId(result?.customerId);
            setRevealed(true);
            setCopied(false);
            setCustomerIdCopied(false);
            clearCopiedResetTimeout();
            clearCustomerIdCopiedResetTimeout();
        } catch {
            log.setDictionaryMessage(ALL_MESSAGES.API_LICENSE_KEY_RETRIEVE_ERROR);
        } finally {
            setRetrieving(false);
        }
    };

    const handleCopyClick = async () => {
        await navigator.clipboard.writeText(licenseKey);
        setCopied(true);
        clearCopiedResetTimeout();
        copiedResetTimeoutRef.current = setTimeout(() => setCopied(false), COPIED_RESET_DELAY_MS);
    };

    const handleCopyCustomerIdClick = async () => {
        await navigator.clipboard.writeText(customerId);
        setCustomerIdCopied(true);
        clearCustomerIdCopiedResetTimeout();
        customerIdCopiedResetTimeoutRef.current = setTimeout(() => setCustomerIdCopied(false), COPIED_RESET_DELAY_MS);
    };

    return (
        <>
            <ApplicationMessages
                scopes={[FORM_NAME]}
                messageType="applicationError"
                messageIds={[ALL_MESSAGES.API_LICENSE_KEY_RETRIEVE_ERROR]}
            />
            <s-box class="mb20" data-testid="api-license-key-retrieve-section">
                <h2 className="margin-top-none margin-bottom-xs">
                    <LanguageStringResolver string='apiLicenseKey_RetrieveSectionTitle' />
                </h2>
                <div className="form__field-wrapper input-container mb20" data-testid="api-license-key-customer-id-wrapper">
                    <label
                        className="form__field-label mt15"
                        htmlFor="apiLicenseKeyCustomerIdField"
                    >
                        <LanguageStringResolver string='apiLicenseKey_CustomerIdLabel' />
                    </label>
                    <div className="mb15" style={{ position: 'relative', maxWidth: '20rem' }}>
                        <input
                            id="apiLicenseKeyCustomerIdField"
                            className="form__field-input"
                            type="text"
                            readOnly
                            data-testid="api-license-key-customer-id-field"
                            style={{ paddingInlineEnd: '2.25rem' }}
                            value={revealed ? (customerId || '') : MASKED_CUSTOMER_ID}
                        />
                        {revealed && (
                            <>
                                <button
                                    type="button"
                                    id="copy-customer-id-button"
                                    className="icon ghost"
                                    data-testid="copy-customer-id-button"
                                    aria-label={LANGUAGE_CONTROLLER.format(props.languages, 'apiLicenseKey_CustomerIdCopyButtonAriaLabel')}
                                    style={{ position: 'absolute', insetInlineEnd: '0.25rem', top: '50%', transform: 'translateY(-50%)' }}
                                    onClick={handleCopyCustomerIdClick}
                                >
                                    <s-icon name={customerIdCopied ? 'clipboard-check' : 'copy'} aria-hidden="true"></s-icon>
                                </button>
                                <STooltip triggerId="copy-customer-id-button" tooltipId="copy-customer-id-tooltip" placement="top">
                                    <span slot="content">
                                        <LanguageStringResolver string={customerIdCopied ? 'apiLicenseKey_CustomerIdCopySuccessTooltip' : 'apiLicenseKey_CustomerIdCopyButtonTooltip'} />
                                    </span>
                                </STooltip>
                            </>
                        )}
                    </div>
                </div>
                <div className="form__field-wrapper input-container mb20">
                    <label
                        className="form__field-label mt15"
                        htmlFor="apiLicenseKeyField"
                    >
                        <LanguageStringResolver string='apiLicenseKey_FieldLabel' />
                    </label>
                    <div className="mb15" style={{ position: 'relative', maxWidth: '20rem' }}>
                        <input
                            id="apiLicenseKeyField"
                            className="form__field-input"
                            type="text"
                            readOnly
                            data-testid="api-license-key-field"
                            style={{ paddingInlineEnd: '2.25rem' }}
                            value={revealed ? (licenseKey || '') : MASKED_LICENSE_KEY}
                        />
                        {revealed && (
                            <>
                                <button
                                    type="button"
                                    id="copy-license-key-button"
                                    className="icon ghost"
                                    data-testid="copy-license-key-button"
                                    aria-label={LANGUAGE_CONTROLLER.format(props.languages, 'apiLicenseKey_CopyButtonAriaLabel')}
                                    style={{ position: 'absolute', insetInlineEnd: '0.25rem', top: '50%', transform: 'translateY(-50%)' }}
                                    onClick={handleCopyClick}
                                >
                                    <s-icon name={copied ? 'clipboard-check' : 'copy'} aria-hidden="true"></s-icon>
                                </button>
                                <STooltip triggerId="copy-license-key-button" tooltipId="copy-license-key-tooltip" placement="top">
                                    <span slot="content">
                                        <LanguageStringResolver string={copied ? 'apiLicenseKey_CopySuccessTooltip' : 'apiLicenseKey_CopyButtonTooltip'} />
                                    </span>
                                </STooltip>
                            </>
                        )}
                    </div>
                    <button
                        type="button"
                        className={retrieving ? 'primary loading mt10' : 'primary mt10'}
                        data-testid="retrieve-license-key-button"
                        disabled={retrieving}
                        onClick={handleToggleClick}
                    >
                        <LanguageStringResolver string={revealed ? 'apiLicenseKey_HideButton' : 'apiLicenseKey_RetrieveButton'} />
                    </button>
                </div>
            </s-box>
        </>
    );
};


export default RetrieveLicenseKeySection;
